import React, { useMemo } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Flex,
} from '@/components/ui';
import { DataTable } from '@/components/data-table';
import { IconDownload, IconHistory } from '@tabler/icons-react';
import { ColumnDef } from '@tanstack/react-table';
import { WhitelistVersion } from '@/utils/singleWhitelistTransformers';

interface WhitelistVersionsTableProps {
  versions: WhitelistVersion[];
  onDownload: (version: WhitelistVersion) => void;
  loading?: boolean;
  currentWhitelistId?: number | null;
}

export const WhitelistVersionsTable: React.FC<WhitelistVersionsTableProps> = ({
  versions,
  onDownload,
  loading = false,
  currentWhitelistId,
}) => {
  void currentWhitelistId;
  // Sort versions by date (newest first) and mark the most recent as active
  const sortedVersions = useMemo(() => {
    const sorted = [...versions].sort((a, b) => {
      const dateA = new Date(a.uploadDate).getTime();
      const dateB = new Date(b.uploadDate).getTime();
      return dateB - dateA; // Newest first
    });
    return sorted;
  }, [versions]);
  const columns: ColumnDef<WhitelistVersion>[] = useMemo(
    () => [
      {
        id: 'filename',
        accessorFn: (row: WhitelistVersion) => row.filename,
        header: 'File',
        meta: {
          cell: {
            variant: 'fileName',
            showIcon: true,
            endSlot: (row: WhitelistVersion, rowIndex?: number) =>
              rowIndex === 0 ? (
                <Badge color="green" size="small">
                  Current
                </Badge>
              ) : null,
          },
          size: '1fr',
          minSize: 150,
          required: true,
        },
      },
      {
        accessorKey: 'contentType',
        header: 'Type',
        meta: {
          cell: {
            variant: 'mimeType',
          },
          size: 80,
          minSize: 150,
        },
      },
      {
        accessorKey: 'size',
        header: 'Size',
        meta: {
          cell: {
            variant: 'fileSize',
          },
          size: 100,
        },
      },
      {
        accessorKey: 'uploadedBy',
        header: 'Uploaded By',
        meta: {
          cell: {
            variant: 'text',
          },
          size: 300,
          minSize: 200,
        },
      },
      {
        accessorKey: 'uploadDate',
        header: 'Date',
        meta: {
          cell: {
            variant: 'datetime',
          },
          // minSize: 150,
          size: 'auto',
        },
      },

      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => (
          <Flex align="center" gap="1" justify="center">
            <Button
              isIconOnly
              variant="text"
              size="xsmall"
              onClick={() => onDownload(row.original)}
              startIcon={<IconDownload />}
              title="Download version"
            />
          </Flex>
        ),
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: false,
        meta: {
          size: 'auto',
          systemColumn: true,
        },
      },
    ],
    [onDownload]
  );

  return (
    <Card
      disablePadding
      style={{ width: '100%' }}
      className="whitelist-versions-table"
    >
      <CardHeader
        cardTitle="Version History"
        description="Previous versions of the whitelist file"
        // startSlot={<IconHistory />}
      />

      <CardContent>
        <DataTable
          scrollMode="table"
          noScrollArea
          data={sortedVersions}
          columns={columns}
          enableSorting={true}
          enableFiltering={false}
          enablePagination={false}
          enableColumnVisibility={false}
          enableRowSelection={false}
          loading={loading}
          emptyMessage="No version history available"
          // emptyDescription="Upload a file to start tracking versions" //ts
          emptyIcon={<IconHistory />}
          getRowId={(row: WhitelistVersion) => row.id.toString()}
        />
      </CardContent>
    </Card>
  );
};
